import { useState } from 'react';
import { Document } from '../components/document-list-vault/types';
import { NWClient } from '../../../client/NWClient';
import { ROUTES } from '../../../common/constants/routes';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchDocuments } from '../../../slices/documents/documentsSlice';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { showMessage } from '../../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import { sanitizeRedirectUrl } from '../../../common/helpers/sanitize';
import { useErrorMessage } from '../../../common/hooks/useErrorMessage';

export const useDocumentDuplicating = () => {
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const currentUser = useAppSelector(selectUser);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const path = location.pathname;
  const [pendingStatus, setPendingStatus] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [document, setDocument] = useState<Document | null>(null);

  const handleDuplicate = (id: number, latest: boolean) => {
    setPendingStatus(true);
    setDialogOpen(false);
    const data = {
      filler: 'data',
    };
    NWClient.post(`document-upload/${id}/duplicate`, data, true, `?latest=${latest}`)
      .then(() => {
        dispatch(fetchDocuments({ data: { user: currentUser.id } }));
        toast.success(t('messages.documentDuplicated'));
      })
      .catch((error) => {
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
        ) {
          dispatch(showMessage(t('messages.monthLimitReached')));
        } else if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.userNotAssociatedWithCompany')
        ) {
          navigate(
            `${ROUTES.COMPANY_CREATE}?backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`
          );
        } else {
          toast.error(getErrorMessage(error));
        }
      })
      .finally(() => {
        setPendingStatus(false);
      });
  };

  const handleDuplicateLatestVersion = (doc: Document) => {
    handleDuplicate(doc.id, true);
  };

  const handleDuplicateOriginalDocument = (doc: Document) => {
    handleDuplicate(doc.id, false);
  };

  return {
    dialogOpen,
    setDialogOpen,
    document,
    setDocument,
    pendingStatus,
    setPendingStatus,
    handleDuplicateOriginalDocument,
    handleDuplicateLatestVersion,
  };
};
